import { observable, makeObservable } from 'mobx'
import { v4 as uuidv4 } from 'uuid'
import { evaluate, parse } from 'mathjs'
import * as R from 'ramda'

class PageQuestionChart {
    @observable chartType

    @observable variables

    @observable formula

    @observable showDisplayText = false

    @observable questions

    constructor({
        questionId,
        chartId,
        chartType,
        title,
        set1,
        icon,
        variables,
        formula,
        displayText,
        kpiType,
        questions = [],
        ranges,
        rangeDisplayText,
        tooltipText,
    }) {
        makeObservable(this)
        this.questionId = questionId
        this.chartId = chartId
        this.chartType = chartType
        this.title = title
        this.set1 = set1
        this.icon = icon
        this.variables = variables
        this.formula = formula
        this.displayText = displayText
        this.showDisplayText = !!displayText
        this.kpiType = kpiType
        this.questions = questions.map((q) => {
            q.uuid = uuidv4()
            return q
        })
        this.ranges = ranges
        this.rangeDisplayText = rangeDisplayText
        this.tooltipText = tooltipText
    }

    changeChartType(chartType) {
        if (chartType !== 'percentageBarChartHorizontal') {
            this.variables = {}
            this.formula = undefined
        }

        if (chartType !== 'radarChart') {
            this.questions = undefined
        } else if (chartType === 'radarChart') {
            this.questions = []
        }

        this.chartType = chartType
    }

    changeTitle(title) {
        this.title = title
    }

    changeRanges(ranges) {
        this.ranges = ranges.map((r) => ({
            rangeText: r.rangeText,
            min: Number(R.or(r.min, 0)),
            max: Number(R.or(r.max, 0)),
        }))
    }

    changeRangeDisplayText(rangeDisplayText, locale) {
        this.rangeDisplayText = { ...this.rangeDisplayText, [locale]: rangeDisplayText }
    }

    changeTooltipText(tooltipText, locale) {
        this.tooltipText = { ...this.tooltipText, [locale]: tooltipText }
    }

    changeDisplayText(displayText) {
        this.displayText = displayText
    }

    changeShowDisplayText() {
        if (!this.showDisplayText) {
            this.showDisplayText = true
            return
        }
        this.displayText = undefined
        this.showDisplayText = false
    }

    changeIcon(icon) {
        this.icon = icon
    }

    changeVariables = (variables) => {
        this.variables = variables
    }

    changeFormula = (formula) => {
        this.formula = formula
    }

    setVariable = (variableKey, value) => {
        this.variables = { ...this.variables, [variableKey]: value }
    }

    removeVariable = (value) => {
        for (const key in this.variables) {
            if (this.variables[key].split('|')[0] == value) delete this.variables[key]
        }
    }

    addQuestion = () => {
        const questions = this.questions.slice()
        questions.push({
            uuid: uuidv4(),
            id: '',
            questionId: '',
            label: '',
        })

        this.questions = questions
    }

    removeQuestion = (index) => {
        this.questions.splice(index, 1)
        console.log('removeQuestion', JSON.stringify(this.questions, null, 4))
    }

    changeFormula= (value) => {
        this.formula = value
    }

    testFormula = (scope = {}, testFormula = this.formula) => {
        if (!testFormula) return { success: true, message: 'Formula Valid', title: this.title }
        const formulaScope = R.clone(scope)

        if (this.variables && Object.keys(this.variables).length) {
            Object.keys(this.variables).forEach((key) => { formulaScope[key] = scope[key] || 0 })
        }

        const parsedFormula = parse(testFormula)
        const usedVariables = parsedFormula.filter((node) => node.isSymbolNode)

        if (usedVariables && usedVariables.length) {
            if (!this.variables) {
                return { success: false, message: 'Formula Error - no variable mapping.', title: this.title }
            }

            const validVariables = usedVariables.every((v) => (
                R.pathOr(false, [[v.name]], this.variables)
            ))

            if (!validVariables) {
                return { success: false, message: 'Formula Error - invalid variables.', title: this.title }
            }
        }

        try {
            const formulaResult = evaluate(testFormula, formulaScope)

            return {
                success: true,
                message: `Formula Valid - result: ${formulaResult}`,
                title: this.title,
            }
        } catch (e) {
            return { success: false, message: 'Formula Error - mathematical error.', title: this.title }
        }
    }

    toJSON() {
        return {
            title: this.title,
            questionId: this.questionId,
            set1: this.set1,
            icon: this.icon,
            chartType: this.chartType,
            chartId: this.chartId,
            variables: this.variables,
            formula: this.formula,
            displayText: this.displayText,
            kpiType: this.kpiType,
            questions: this.chartType === 'radarChart' ? this.questions : undefined,
            ranges: this.ranges,
            rangeDisplayText: this.rangeDisplayText,
            tooltipText: this.tooltipText,
        }
    }
}

export default PageQuestionChart
